/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import DateFnsUtils from '@date-io/date-fns';
import { shortDateUIFormat, shortDateISO8601ServerFormat } from '../../settings';

/**
 * A pure function that receives a date and returns a short datetime string with the UI format
 * @param {Date} date - a javascript Date object
 * @returns {string} - a string with the following format DD/MM/YYYY HH:MM
 */

const iSO8601datePattern = /^([12]\d{3}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01]))/;
const fnsUtils = new DateFnsUtils();

export function shortFormatDatetime(date, format) {
  const jsDate = date instanceof Date ? date : new Date(date);
  let dateString = '';
  if (!Number.isNaN(jsDate.getTime())) {
    const minutes = jsDate.getMinutes() < 10 ? '0'.concat(jsDate.getMinutes()) : jsDate.getMinutes();
    dateString = `${fnsUtils.format(jsDate, format || shortDateUIFormat)} ${jsDate.getHours()}:${minutes}`;
  }
  return dateString;
}

/**
 * A function that receives a date and returns a short date string with the UI format
 * In case the param is already a short date string, no need to use js dates in order to avoid timezone issues
 * @param date - a javascript object
 * @param format - date format
 * @returns {string} - a string with the formatted date
 */
export function shortFormatDate(date, format) {
  if (typeof date === 'string' || date instanceof String) {
    if (date.match(iSO8601datePattern) != null) {
      return formatISO8601ShortDateString(date, format);
    }
  }
  const newDate = new Date(date);
  if (Number.isNaN(newDate.getDate())) {
    return '';
  }
  return fnsUtils.format(date, format || shortDateUIFormat);
}

/**
 * function that receives a ISO 8601 short date string (yyyy-MM-dd) and returns a string with the specified UI format
 * @param isoString - a String short date format yyyy-MM-dd
 * @param format - a String short date format (from frontendCustomization.json or if not specified from settings.js)
 * @returns {string} - a string with ith the desired UI format
 */
function formatISO8601ShortDateString(isoString, format) {
  const date = new Date(isoString.substring(0, 4), isoString.substring(5, 7) - 1, isoString.substring(8, 10));
  return fnsUtils.format(date, format || shortDateUIFormat);
}

/**
 * A function only for datePicker component that receives a date and returns a ISO 8601 short date string
 * Needed in order to avoid timezone issues
 * @param {Date} date - a javascript Date object
 * @returns {string|null} - a string with the following format yyyy-MM-dd
 */
export function jsDateToLocalISO8601DateString(date) {
  const isValidDate = Date.parse(date);
  if (!date || Number.isNaN(isValidDate)) {
    return null;
  }
  const dateValue = new Date(date);
  return [String(dateValue.getFullYear()), String(101 + dateValue.getMonth()).substring(1), String(100 + dateValue.getDate()).substring(1)].join('-');
}

/**
 * A function only for datePicker component that receives a string and returns a date
 * Needed in order to avoid timezone issues
 * @param {string} stringDate - a javascript Date object
 * @returns {Date} - a js date
 */
export function dateStringToLocalDate(stringDate) {
  if (!stringDate) return null;
  return fnsUtils.parse(stringDate, shortDateISO8601ServerFormat);
}

/**
 * A function only for datePicker component that receives a string with a number of months and returns a date
 * with the value Today - number of months. Used in datePicker to limit the minDate
 * @param {string} maxPastMonths - schema maxPastMonths
 * @returns {Date} - datePicker minDate
 */
export function getMinDate(maxPastMonths) {
  if (maxPastMonths) {
    const months = Number(maxPastMonths);
    const date = new Date();
    date.setMonth(date.getMonth() - months);
    return date;
  }
  return undefined;
}

export function formatIfDate(value, format) {
  if (value === undefined) return '';
  const date = Date.parse(value?.split(' ')[0]);
  return Number.isNaN(date) ? value : fnsUtils.format(date, format || shortDateUIFormat);
}
